import { useEffect, useRef } from 'react';
import { Button } from './button.js';

interface AlertDialogProps {
  open: boolean;
  title: string;
  children: React.ReactNode;
  /** Confirm button label. Omit `onConfirm` to render a cancel-only dialog. */
  confirmLabel?: React.ReactNode;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm?: () => void;
  onCancel: () => void;
}

/**
 * Shared destructive-confirmation dialog (CLAUDE.md §6.1: destructive confirmations use
 * <AlertDialog>, never anything else). Proper alertdialog semantics: role="alertdialog",
 * aria-modal, Escape cancels, initial focus lands on the cancel button so a stray Enter
 * never confirms.
 */
export function AlertDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: AlertDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" onClick={onCancel} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-md rounded-lg border bg-card shadow-xl"
      >
        <div className="px-5 py-4 border-b">
          <h2 className="text-sm font-semibold">{title}</h2>
        </div>
        <div className="px-5 py-4 text-sm">{children}</div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t">
          <Button ref={cancelRef} type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          {onConfirm && confirmLabel && (
            <Button
              type="button"
              variant={destructive ? 'destructive' : 'primary'}
              onClick={onConfirm}
              disabled={busy}
            >
              {confirmLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
